import { useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { toast } from "react-toastify";
import {
  WhatsApp,
  ContentCopy,
  PhoneDisabled,
  EditOutlined,
  AddOutlined,
} from "@mui/icons-material";
import {
  buildWhatsappLink,
  CustomerContact,
  resolveCustomerContact,
} from "@/modules/common/utils/customer-contact";

/** Uses only permitted contact data supplied by the caller; technical provider IDs never enter the UI. */
export default function CustomerContactDisplay({
  contacts,
  hidden = false,
  actions = true,
  mask = false,
  onManagePhones,
  stacked = false,
}: {
  contacts?: readonly CustomerContact[] | null;
  hidden?: boolean;
  actions?: boolean | "copy";
  mask?: boolean;
  onManagePhones?: () => void;
  stacked?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const contact = resolveCustomerContact(contacts, hidden);
  const link = buildWhatsappLink(contact);
  const restricted = hidden || contact.kind === "empty";
  const unverifiedReason =
    "Phone number could not be verified. Check the number and country code before calling.";
  const label =
    mask && !revealed && !restricted
      ? `${contact.label.slice(0, -3)}***`
      : contact.label;
  const copyLabel =
    contact.kind === "whatsapp" ? "Copy username" : "Copy phone number";
  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(contact.username ?? contact.label);
      toast.success(
        contact.kind === "whatsapp" ? "Username copied" : "Phone number copied",
        {
          position: "bottom-left",
          autoClose: 2000,
          toastId: "customer-contact-copy",
        },
      );
    } catch {
      toast.error("Could not copy. Please select and copy the contact.", {
        position: "bottom-left",
        toastId: "customer-contact-copy-error",
      });
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap:
          stacked || (onManagePhones && !contact.phone) ? "wrap" : "nowrap",
        width: stacked ? "100%" : undefined,
        minWidth: 0,
        maxWidth: "100%",
      }}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setRevealed(false);
      }}
    >
      {contact.kind === "whatsapp" && !stacked && (
        <WhatsApp
          sx={{ fontSize: 16, flexShrink: 0 }}
          aria-label="WhatsApp username"
        />
      )}
      <Typography
        component="span"
        tabIndex={mask && !restricted ? 0 : undefined}
        title={
          contact.kind === "unverified-phone"
            ? `${label} — ${unverifiedReason}`
            : label
        }
        dir="ltr"
        sx={{
          overflow: stacked ? "visible" : "hidden",
          textOverflow: stacked ? undefined : "ellipsis",
          whiteSpace: stacked ? "normal" : "nowrap",
          overflowWrap: stacked ? "anywhere" : undefined,
          flexBasis: stacked ? "100%" : undefined,
          minWidth: 0,
          fontSize: "inherit",
          userSelect: "text",
        }}
      >
        {label}
      </Typography>
      {actions && !restricted && (
        <>
          <Tooltip title={copyLabel}>
            <IconButton
              size="small"
              aria-label={copyLabel}
              onClick={handleCopy}
              sx={{ flexShrink: 0, minWidth: 32, minHeight: 32 }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {actions !== "copy" &&
            (stacked && contact.kind === "whatsapp" ? (
              <Button
                size="small"
                color="inherit"
                startIcon={<WhatsApp />}
                disabled={!link}
                onClick={(event) => {
                  event.stopPropagation();
                  if (link) window.open(link, "_blank", "noopener,noreferrer");
                }}
                sx={{ textTransform: "none", minHeight: 32 }}
              >
                Open WhatsApp
              </Button>
            ) : (
              <Tooltip title={link ? "Open WhatsApp" : unverifiedReason}>
                <Box
                  component="span"
                  sx={{ display: "inline-flex", flexShrink: 0 }}
                >
                  <IconButton
                    size="small"
                    aria-label="Open WhatsApp"
                    disabled={!link}
                    sx={{ flexShrink: 0, minWidth: 32, minHeight: 32 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (link)
                        window.open(link, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <WhatsApp fontSize="inherit" />
                  </IconButton>
                </Box>
              </Tooltip>
            ))}
        </>
      )}
      {actions === true && !hidden && !contact.phone && (
        <Tooltip
          title={
            contact.kind === "unverified-phone"
              ? unverifiedReason
              : contact.kind === "empty"
                ? "No contact details"
                : "Phone number unavailable"
          }
        >
          <Box component="span" sx={{ flexShrink: 0 }}>
            <IconButton
              size="small"
              disabled
              aria-label="Phone number unavailable"
            >
              <PhoneDisabled fontSize="inherit" />
            </IconButton>
          </Box>
        </Tooltip>
      )}
      {onManagePhones &&
      !hidden &&
      !contact.phone &&
      contact.kind !== "unverified-phone" ? (
        <Box sx={{ flexBasis: "100%", minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<AddOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              onManagePhones();
            }}
            sx={{ textTransform: "none" }}
          >
            Add phone number
          </Button>
        </Box>
      ) : (
        onManagePhones &&
        !hidden && (
          <Tooltip title="Manage phone numbers">
            <IconButton
              size="small"
              aria-label="Manage phone numbers"
              onClick={(event) => {
                event.stopPropagation();
                onManagePhones();
              }}
            >
              <EditOutlined fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )
      )}
    </Box>
  );
}
